import type { TolgeePlugin } from '@tolgee/react'
import { getCachedOrFetchNamespace, refreshTranslationLanguage } from './cache'
import { languages, type Language, type Namespace } from './generated/i18n-meta'

declare const __DEV__: boolean | undefined

type CachedBackendOptions = {
  prefix: string
  cacheKey?: string
}

export function CachedBackend(options: CachedBackendOptions): TolgeePlugin {
  return (tolgee, tools) => {
    const { prefix, cacheKey } = options

    tolgee.on('language', (event) => {
      const language = event.value as Language

      if (!language || !languages.includes(language)) {
        return
      }

      void (async () => {
        try {
          const changed = await refreshTranslationLanguage(language, prefix, cacheKey)

          if (!changed) {
            return
          }

          await tolgee.loadRecords(tolgee.getRequiredDescriptors(language), {
            useCache: false,
          })
        } catch (error) {
          if (__DEV__) {
            console.error('Error refreshing translations:', error)
          }
        }
      })()
    })

    tools.addBackend({
      async getRecord({ language, namespace }) {
        const ns = (namespace ?? 'common') as Namespace

        try {
          return (
            (await getCachedOrFetchNamespace(
              language as Language,
              ns,
              prefix,
              cacheKey
            )) ?? undefined
          )
        } catch (error) {
          if (__DEV__) {
            console.error('Error fetching translations:', error)
          }

          return undefined
        }
      },
    })

    return tolgee
  }
}
